import * as React from 'react';
import { Modal, Typography, Card, Box, Grid, IconButton, Icon, Button, Select, MenuItem, Tooltip, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CancelIcon from '../../../icons/cancelIcon.svg';
import { QuickPayTypeDto } from '@inosio/payments-sdk';
import { selectBillingState } from '../../billingConfiguration/sagas/billingSlice';
import { useDispatch, useSelector } from 'react-redux';
import AmountInputConfig from './AmountInputConfig';
import HelpIcon from '@mui/icons-material/Help';
import PaymentTypeConfig from './PaymentTypeConfig';
import { getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import NumberFormat from 'react-number-format';
import { convertCentsToDollars } from '../../../shared/util/converters';
import { useEffect } from 'react';

const useStyles = makeStyles({
  modalContainer: {
    background: '#FFFFFF',
    boxShadow: 'none',
    borderRadius: '24px',
  },
  titleText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    textAlign: 'center',
  },
  detailsText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '18px !important',
    lineHeight: '21px !important',
    color: '#000000 !important',
  },
  buttonContainer: {
    position: 'absolute',
    bottom: '20px',
    left: '24px',
    right: '24px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  inputRoot: {
    '& .MuiOutlinedInput-root': {
      height: '27px',
      fontSize: 12,
      marginLeft: '16px',
      '& fieldset': {
        borderRadius: '24px',
        borderColor: '#F1F0F5',
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
      },
    },
  },
  errorText: {
    fontFamily: 'Work Sans !important',
    fontSize: '12px !important',
    lineHeight: '22px !important',
    color: '#ff0000 !important',
    marginLeft: '10px !important',
  },
});

export enum QuickPayModalType {
  Create = 'Create',
  Edit = 'Edit',
}

interface QuickPayModalProps {
  open: boolean;
  mode: QuickPayModalType;
  quickPayItem?: QuickPayTypeDto;
  closeCallback: () => void;
  showSpinnerModal: () => void;
}

export default function QuickPayModal(props: QuickPayModalProps) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    minHeight: '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };

  const { selectedPropertyGroupAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const [label, setLabel] = React.useState(props.quickPayItem?.Label ?? '');
  const [labelError, setLabelError] = React.useState('');
  const [minimum, setMinimum] = React.useState(props.quickPayItem?.MinimumPaymentAmount ?? 0);
  const [payMethods, setPayMethods] = React.useState(props.quickPayItem?.AllowedPaymentTypes ?? []);
  const [payMethodsError, setPayMethodsError] = React.useState('');
  const [depositBank, setDepositBank] = React.useState(props.quickPayItem?.DepositBankUuid ?? '');
  const [depositError, setDepositError] = React.useState('');

  const { availableBankAccounts } = useSelector(selectBillingState);

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  );

  const handleSave = () => {
    setLabelError('');
    setPayMethodsError('');
    setDepositError('');

    if (!label) setLabelError('Description is required.');
    if (label.length > 20) setLabelError('The maximum length is 20.');
    if (payMethods.length === 0) setPayMethodsError('At least one payment method is required.');
    if (!depositBank) setDepositError('Bank account is required.');

    if (label && label.length <= 20 && payMethods.length > 0 && depositBank) {
      props.showSpinnerModal();

      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
          productTypeId: 1,
          updatedPmConfig: {
            QuickPayTypes: [
              {
                Uuid: props.quickPayItem?.Uuid,
                Label: label,
                DepositBankUuid: depositBank,
                MinimumPaymentAmount: minimum,
                AllowedPaymentTypes: payMethods,
              },
            ],
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (props.quickPayItem?.MaximumPaymentAmount && minimum > props.quickPayItem?.MaximumPaymentAmount) {
      setLabelError(`Minimum cannot exceed the set maximum of $${new Intl.NumberFormat().format(convertCentsToDollars(props.quickPayItem?.MaximumPaymentAmount))}`);
    }
  }, [minimum]);

  return (
    <div className={`${classes.modalContainer}`}>
      <Modal open={props.open} onClose={() => props.closeCallback()} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description">
        <Card sx={style}>
          <Grid container>
            <Grid item xs={2}></Grid>
            <Grid item xs={8}>
              <Typography className={classes.titleText} sx={{ mt: 1 }}>
                {props.mode === QuickPayModalType.Create ? 'Create a new payment type' : 'Edit payment type'}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <IconButton sx={{ ml: '30px' }} onClick={() => props.closeCallback()}>
                <Icon>
                  <img src={CancelIcon} alt="" width={24} height={24} />
                </Icon>
              </IconButton>
            </Grid>
          </Grid>

          <Box>
            <Box sx={{ mx: 1, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <Tooltip title="This label will be shown to the payer so they can choose what they will pay for." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', mr: '9px' }} />
                </Tooltip>
                {getTypography('Description:', 400, '14px')}
                <TextField
                  name={'Label'}
                  value={label}
                  onChange={(e) => {
                    setLabel(e.target.value);
                  }}
                  classes={{
                    root: classes.inputRoot,
                  }}
                />
              </Box>
              {labelError && <Typography className={classes.errorText}>{labelError}</Typography>}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <Tooltip title="The minimum amount you'll accept for this payment type." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', mr: '9px' }} />
                </Tooltip>
                {getTypography('Minimum Amount:', 400, '14px')}
                <Box sx={{ width: 100, mx: 2 }}>
                  <AmountInputConfig
                    name="MinimumPaymentAmount"
                    value={minimum}
                    onChange={(c: string, v: any) => {
                      setMinimum(v);
                    }}
                    error={''}
                    onError={() => {}}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <Tooltip title="The maximum amount you can accept for this payment type." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', mr: '9px' }} />
                </Tooltip>
                {getTypography('Maximum Amount:', 400, '14px')}
                <Box sx={{ width: 100, mx: 2 }}>
                  <NumberFormat
                    readOnly={true}
                    value={props.quickPayItem?.MaximumPaymentAmount ? props.quickPayItem?.MaximumPaymentAmount / 100 : 0}
                    thousandSeparator
                    isNumericString
                    fixedDecimalScale
                    decimalScale={2}
                    prefix="$"
                    placeholder="$0.00"
                    style={{
                      width: '100px',
                      height: '27px',
                      textAlign: 'center' as 'center',
                      borderRadius: '24px',
                      border: '1px solid #F1F0F5',
                      padding: '0 8px',
                      outline: 'none !important',
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <PaymentTypeConfig
                  name="AllowedPaymentTypes"
                  label="Allowed payment methods:"
                  description="Select which payment methods are allowed to be used by the payer."
                  value={payMethods}
                  onChange={(c: string, v: any) => {
                    setPayMethods(v);
                  }}
                />
              </Box>
              {payMethodsError && (
                <Typography className={classes.errorText} sx={{ marginTop: '-10px', marginBottom: '10px' }}>
                  {payMethodsError}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                <Tooltip title="Select the bank account you want these payments to be sent to. If you need a different bank account add it first then come back to select it." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', mr: '9px' }} />
                </Tooltip>
                {getTypography('Bank Account:', 400, '14px')}
                <Select
                  value={depositBank ?? ''}
                  displayEmpty
                  sx={{ borderRadius: '24px', height: '31px', mx: 2, '.MuiTypography-root': { fontSize: '14px' } }}
                  onChange={(e) => {
                    setDepositBank(e.target.value);
                  }}
                >
                  <MenuItem disabled value={''}>
                    <Typography>Select account</Typography>
                  </MenuItem>
                  {availableBankAccounts.map((bankAccount) => (
                    <MenuItem value={bankAccount.bankAccountUuid} key={bankAccount.bankAccountUuid} selected={bankAccount.bankAccountUuid === depositBank}>
                      <Typography>****{bankAccount.last4}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {depositError && <Typography className={classes.errorText}>{depositError}</Typography>}
            </Box>

            <div className={classes.buttonContainer}>
              <Button
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  width: '87px',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                  border: '2px solid #DB3939',
                  padding: '5px 8px',
                  color: '#DB3939',
                }}
                onClick={() => props.closeCallback()}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  width: '87px',
                  marginLeft: '8px',
                  background: '#39A2DB',
                  borderRadius: '24px',
                  textTransform: 'capitalize',
                }}
                disabled={!!props.quickPayItem?.MaximumPaymentAmount && minimum > props.quickPayItem?.MaximumPaymentAmount}
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </Box>
        </Card>
      </Modal>
    </div>
  );
}
